import { Pipe, PipeTransform } from '@angular/core';
import moment from 'moment-timezone';
import { LocaleTimeUtils } from '../utils/locale-time.utils';

export type LocaleDateMode = 'date' | 'time' | 'datetime';

/**
 * Localized date/time pipe with support for locale, timezone and 12h/24h formats.
 * Modes: 'date', 'time', 'datetime'.
 * If no timezone is provided, the timezone in the input string is used and browser timezone is ignored.
 *
 * Usage: {{ value | localeDate: mode : timezone : locale }}
 */
@Pipe({ name: 'localeDate', standalone: true })
export class LocaleDatePipe implements PipeTransform {
  transform(value: string | number | Date | null | undefined, mode: LocaleDateMode, timezone: string, locale: string, force24h = false, showTz = true): string {
    if (value == null || value === '' || !locale) return '';

    const m = timezone ? moment(value).tz(timezone) : moment.parseZone(value);
    const localized = m.locale(locale);
    if (!localized.isValid()) return '';

    const is24h = force24h || LocaleTimeUtils.is24HourLocale(locale);
    const timePart = is24h ? 'HH:mm' : 'hh:mm A';
    const tzSuffix = timezone && showTz ? ' z' : '';
    const timeZone = timezone ? localized.tz() : undefined;
    const datePart = LocaleTimeUtils.formatDate(localized.toDate(), locale, timeZone ? { timeZone } : {});

    switch (mode) {
      case 'date':
        return datePart;
      case 'time':
        return localized.format(`${timePart}${tzSuffix}`);
      case 'datetime':
        return `${datePart}, ${localized.format(`${timePart}${tzSuffix}`)}`;
    }
  }
}
